/**
 * @author        tae@zumper.com (Tae Yoon)
 * @copyright     Copyright (c) 2019, Zumper
 * @description   floorplan selectors
 */

import { PropertyType } from '@zcode/enums'
import type { FloorplanListing, NormalFloorplanListingData } from '@zcode/state-type'
import { isNumber } from '@zcode/utils'

export function selectIsRoomType(
  floorplan: FloorplanListing<NormalFloorplanListingData> | undefined
) {
  if (!floorplan) {
    return false
  }

  const { isStudentHousing, propertyType } = floorplan

  return (
    isStudentHousing &&
    (propertyType === PropertyType.ROOM || propertyType === PropertyType.SHARED_ROOM)
  )
}

export function selectRoomText(
  floorplan: FloorplanListing<NormalFloorplanListingData> | undefined
) {
  const { propertyType, bedrooms } = floorplan || {}

  if (!isNumber(bedrooms) || bedrooms < 0) {
    return ''
  }

  const formattedBedroomType = bedrooms === 0 ? 'Studio' : (`${bedrooms} Bed` as const)

  if (propertyType === PropertyType.SHARED_ROOM) {
    return `Shared ${formattedBedroomType}` as const
  }
  if (propertyType === PropertyType.ROOM) {
    return `Private ${formattedBedroomType}` as const
  }
  return formattedBedroomType
}

export function selectRoomCategoryText(
  floorplan: FloorplanListing<NormalFloorplanListingData> | undefined
) {
  if (!floorplan) {
    return ''
  }

  const { bathrooms, bathroomTxt, maxBathrooms } = floorplan

  const roomText = selectRoomText(floorplan)

  if (!bathrooms && !maxBathrooms) {
    return roomText
  }

  if (bathroomTxt) {
    const bathroomText = bathroomTxt
    if (roomText) {
      return `${roomText} · ${bathroomText}` as const
    }
    return bathroomText
  }

  return roomText
}
